// Miniatur kota LIVE di pulau beranda: tata letak kota asli (grid 60×60 dari /api/public/city/<id>) dipetakan ke dalam poligon wilayahnya,
// digambar dengan sprite mini bergaya pulau (rumah beratap, gedung bertingkat, pohon, air berkilau, jalan garis halus) — bukan kotak-kotak.
// Hanya inti kota yang terbangun yang dipetakan (supaya kota kecil tidak jadi serpihan), lalu di-cache ke kanvas cadangan.
// (Nanti: 1 bangunan penanda "Tahta Jawsel" paling mencolok ditambah sebagai lapisan di atas miniatur.)
import { REGION_GEO, REGION_ORDER } from "../jawselRegions";
import { mapPoint, inPoly } from "./geo";
import { drawTree } from "./sprites";

const cache = { key: "", canvas: null, lampu: null };
const VEGETASI = /pohon|hutan|palem|cemara|bunga|kebun|taman|rumput|semak|sawah|ladang/;
const KEBUN = /kebun|sawah|ladang|bunga|taman/;
const TINGGI = /kost|ruko|toko|mall|kantor|apartemen|hotel|gedung|menara|pabrik|rumah_sakit|sekolah|kampus|masjid|gereja|stasiun|bandara|balai|pasar|stadion/;

export function adaIsi(st) { return ((st.buildings || []).length + (st.water || []).length + (st.roads || []).length) > 0; }

// Bbox sel yang terpakai (inti kota) + padding → dipetakan ke bbox poligon wilayah (porsi 0,86)
function pemeta(r, st) {
  const sel = [...(st.buildings || []).map(b => [b.x, b.y]), ...["roads", "water", "runways"].flatMap(k => (st[k] || []).map(s => s.split(",").map(Number)))];
  let gx0 = Math.min(...sel.map(p => p[0])) - 1, gx1 = Math.max(...sel.map(p => p[0])) + 2, gy0 = Math.min(...sel.map(p => p[1])) - 1, gy1 = Math.max(...sel.map(p => p[1])) + 2;
  const lebar = Math.max(gx1 - gx0, gy1 - gy0, 12), cx = (gx0 + gx1) / 2, cy = (gy0 + gy1) / 2;   // jaga rasio 1:1 agar grid tidak gepeng
  const xs = r.pts.map(p => p[0]), ys = r.pts.map(p => p[1]);
  const rw = (Math.max(...xs) - Math.min(...xs)) * 0.86, rh = (Math.max(...ys) - Math.min(...ys)) * 0.86, skala = Math.min(rw, rh) / lebar;
  return (gx, gy) => [r.center[0] + (gx + 0.5 - cx) * skala, r.center[1] + (gy + 0.5 - cy) * skala];
}

// ---- sprite mini (koordinat layar; s = ukuran 1 sel) ----
function bayangan(ctx, x, y, s, k = 1) { ctx.fillStyle = "rgba(0,0,0,.16)"; ctx.beginPath(); ctx.ellipse(x, y + s * 0.12, s * 0.62 * k, s * 0.24 * k, 0, 0, Math.PI * 2); ctx.fill(); }
function kotakIso(ctx, x, y, w, h, kiri, kanan, atas) {   // balok 2 sisi + atap datar, hidung ke bawah (sesuai kemiringan pulau)
  const dx = w * 0.5, dy = w * 0.26;
  ctx.fillStyle = kiri; ctx.beginPath(); ctx.moveTo(x - dx, y - dy); ctx.lineTo(x, y); ctx.lineTo(x, y - h); ctx.lineTo(x - dx, y - dy - h); ctx.closePath(); ctx.fill();
  ctx.fillStyle = kanan; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + dx, y - dy); ctx.lineTo(x + dx, y - dy - h); ctx.lineTo(x, y - h); ctx.closePath(); ctx.fill();
  ctx.fillStyle = atas; ctx.beginPath(); ctx.moveTo(x - dx, y - dy - h); ctx.lineTo(x, y - h); ctx.lineTo(x + dx, y - dy - h); ctx.lineTo(x, y - 2 * dy - h); ctx.closePath(); ctx.fill();
}
function rumah(ctx, x, y, s, seed) {
  const w = s * 1.15, h = s * 0.7, atap = ["#c95a4a", "#b7472f", "#d8894c", "#8d6e63"][seed % 4];
  bayangan(ctx, x, y, s);
  kotakIso(ctx, x, y, w, h, "#e3ddcf", "#f7f4ec", atap);
  ctx.fillStyle = atap; ctx.beginPath(); ctx.moveTo(x - w * 0.5, y - w * 0.26 - h); ctx.lineTo(x, y - h); ctx.lineTo(x, y - h - s * 0.55); ctx.lineTo(x - w * 0.5, y - w * 0.26 - h - s * 0.3); ctx.closePath(); ctx.fill();   // sisi atap miring
  ctx.fillStyle = "rgba(0,0,0,.12)"; ctx.beginPath(); ctx.moveTo(x, y - h); ctx.lineTo(x + w * 0.5, y - w * 0.26 - h); ctx.lineTo(x + w * 0.5, y - w * 0.26 - h - s * 0.3); ctx.lineTo(x, y - h - s * 0.55); ctx.closePath(); ctx.fill();
}
function gedung(ctx, x, y, s, seed) {
  const w = s * 1.25, h = s * (1.9 + (seed % 3) * 0.55), pal = [["#d9b26b", "#f0cd84", "#f7e2b0"], ["#9fb4c7", "#c6d6e3", "#e4edf4"], ["#d88a6a", "#f0ad8f", "#f8cdb8"]][seed % 3];
  bayangan(ctx, x, y, s, 1.2);
  kotakIso(ctx, x, y, w, h, pal[0], pal[1], pal[2]);
  ctx.fillStyle = "rgba(40,60,90,.35)";   // jendela
  for (let j = 1; j < h / (s * 0.45); j++) { const yy = y - j * s * 0.45; ctx.fillRect(x + w * 0.12, yy - w * 0.1, s * 0.16, s * 0.16); ctx.fillRect(x - w * 0.3, yy - w * 0.0, s * 0.16, s * 0.16); }
}
function kebun(ctx, x, y, s, seed) {
  ctx.fillStyle = ["#9bd47a", "#b8dd7c", "#86c66f"][seed % 3]; ctx.beginPath(); ctx.ellipse(x, y, s * 0.6, s * 0.34, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,.3)"; ctx.beginPath(); ctx.ellipse(x - s * 0.15, y - s * 0.08, s * 0.22, s * 0.1, 0, 0, Math.PI * 2); ctx.fill();
}
function air(ctx, x, y, s) {
  ctx.fillStyle = "#4fa3d9"; ctx.beginPath(); ctx.ellipse(x, y, s * 0.62, s * 0.36, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,.35)"; ctx.beginPath(); ctx.ellipse(x - s * 0.12, y - s * 0.08, s * 0.26, s * 0.1, 0, 0, Math.PI * 2); ctx.fill();
}
function jalan(ctx, daftar, titik, s, warna) {   // garis halus menyambung sel jalan yang bertetangga
  const set = new Set(daftar); ctx.strokeStyle = warna; ctx.lineWidth = Math.max(0.8, s * 0.42); ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.beginPath();
  for (const key of daftar) {
    const [gx, gy] = key.split(",").map(Number), a = titik(gx, gy); if (!a) continue;
    let ada = false;
    for (const [nx, ny] of [[gx + 1, gy], [gx, gy + 1]]) { if (set.has(`${nx},${ny}`)) { const b = titik(nx, ny); if (b) { ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ada = true; } } }
    if (!ada) { ctx.moveTo(a[0], a[1]); ctx.lineTo(a[0] + 0.01, a[1]); }
  }
  ctx.stroke();
}

function renderLapisan(off, transform, v, stats, compact) {
  const ctx = off.getContext("2d");
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, off.width, off.height); ctx.setTransform(transform);
  for (const id of REGION_ORDER) {
    const r = REGION_GEO[id], st = stats[id] || {};
    if (!adaIsi(st)) continue;
    const peta = pemeta(r, st);
    const [ax, ay] = mapPoint(v, ...peta(0, 0), 0), [bx, by] = mapPoint(v, ...peta(1, 0), 0);
    const s = Math.min(compact ? 5 : 9, Math.max(compact ? 1.4 : 2.0, Math.hypot(bx - ax, by - ay)));
    const titik = (gx, gy) => { const [mx, my] = peta(gx, gy); return inPoly(mx, my, r.pts) ? mapPoint(v, mx, my, 0) : null; };
    // dasar kota: lantai tipis krem agar area terbangun terbaca (menyatu lembut dengan rumput)
    ctx.fillStyle = "rgba(255,250,235,.10)";
    for (const key of (st.roads || [])) { const [gx, gy] = key.split(",").map(Number); const p = titik(gx, gy); if (p) { ctx.beginPath(); ctx.arc(p[0], p[1], s * 1.0, 0, Math.PI * 2); ctx.fill(); } }
    jalan(ctx, st.runways || [], titik, s * 1.6, "#8a8781");
    jalan(ctx, st.roads || [], titik, s, "#b3ac9f");
    for (const key of (st.water || [])) { const [gx, gy] = key.split(",").map(Number); const p = titik(gx, gy); if (p) air(ctx, p[0], p[1], s); }
    // bangunan & pohon diurut dari belakang ke depan (y layar); pohon terlalu banyak → disaring agar tidak jadi serpihan
    const semua = (st.buildings || []).map(b => ({ ...b, p: titik(b.x, b.y) })).filter(b => b.p);
    const pohon = semua.filter(b => VEGETASI.test(b.bid || "")), lain = semua.filter(b => !VEGETASI.test(b.bid || ""));
    const maksPohon = compact ? 120 : 260, langkah = Math.max(1, Math.ceil(pohon.length / maksPohon));
    const tampil = [...lain, ...pohon.filter((_, i) => i % langkah === 0)].sort((a, b) => a.p[1] - b.p[1]);
    for (const b of tampil) {
      const seed = (b.x * 31 + b.y * 17) | 0, bid = b.bid || "";
      if (KEBUN.test(bid)) kebun(ctx, b.p[0], b.p[1], s, seed);
      else if (VEGETASI.test(bid)) drawTree(ctx, b.p[0], b.p[1], s * 0.75);
      else if (TINGGI.test(bid)) gedung(ctx, b.p[0], b.p[1], s, seed);
      else rumah(ctx, b.p[0], b.p[1], s, seed);
    }
  }
}

// Lapisan LAMPU MALAM (kanvas cadangan terpisah): jendela rumah/gedung menyala kuning hangat + lampu jalan tiap beberapa sel, dengan pendar lembut.
function lampuTitik(ctx, x, y, r, a) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r * 3.2); g.addColorStop(0, `rgba(255,214,107,${a})`); g.addColorStop(1, "rgba(255,190,80,0)");
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r * 3.2, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#ffe08a"; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
}
function renderLampu(off, transform, v, stats, compact) {
  const ctx = off.getContext("2d");
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, off.width, off.height); ctx.setTransform(transform);
  for (const id of REGION_ORDER) {
    const r = REGION_GEO[id], st = stats[id] || {};
    if (!adaIsi(st)) continue;
    const peta = pemeta(r, st);
    const [ax, ay] = mapPoint(v, ...peta(0, 0), 0), [bx, by] = mapPoint(v, ...peta(1, 0), 0);
    const s = Math.min(compact ? 5 : 9, Math.max(compact ? 1.4 : 2.0, Math.hypot(bx - ax, by - ay)));
    const titik = (gx, gy) => { const [mx, my] = peta(gx, gy); return inPoly(mx, my, r.pts) ? mapPoint(v, mx, my, 0) : null; };
    for (const key of (st.roads || [])) {   // lampu jalan: 1 dari ±4 sel
      const [gx, gy] = key.split(",").map(Number); if ((gx * 7 + gy * 13) % 4) continue;
      const p = titik(gx, gy); if (p) lampuTitik(ctx, p[0] + s * 0.3, p[1] - s * 0.5, s * 0.15, 0.3);
    }
    for (const b of (st.buildings || [])) {
      const bid = b.bid || ""; if (VEGETASI.test(bid)) continue;
      const p = titik(b.x, b.y); if (!p) continue;
      if (TINGGI.test(bid)) { const seed = (b.x * 31 + b.y * 17) | 0, h = s * (1.9 + (seed % 3) * 0.55); for (let j = 1; j < h / (s * 0.45); j++) if ((seed + j) % 3) lampuTitik(ctx, p[0] + (j % 2 ? s * 0.2 : -s * 0.3), p[1] - j * s * 0.45 - s * 0.05, s * 0.12, 0.26); }
      else if ((b.x + b.y) % 3) lampuTitik(ctx, p[0] + s * 0.18, p[1] - s * 0.38, s * 0.14, 0.34);
    }
  }
}

// Gambar lapisan miniatur (dengan cache) di kanvas utama; `bob` = ayunan pulau (hanya geser vertikal)
export function drawMiniKota(ctx, canvas, v, bob, stats, compact) {
  const t = ctx.getTransform();
  const key = `${canvas.width}x${canvas.height}|${v.ox.toFixed(1)},${v.oy.toFixed(1)},${v.s.toFixed(3)}|${stats.__versi || 0}`;
  if (cache.key !== key) {
    for (const k of ["canvas", "lampu"]) if (!cache[k] || cache[k].width !== canvas.width || cache[k].height !== canvas.height) { cache[k] = document.createElement("canvas"); cache[k].width = canvas.width; cache[k].height = canvas.height; }
    renderLapisan(cache.canvas, t, v, stats, compact); renderLampu(cache.lampu, t, v, stats, compact); cache.key = key;
  }
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(cache.canvas, 0, bob * t.d); ctx.restore();
}

// Lampu kota digambar SETELAH selubung malam (render.js) agar tetap terang; `malam` 0–1, berdenyut pelan
export function drawLampuKota(ctx, bob, malam, ms) {
  if (!cache.lampu || malam < 0.05) return;
  const t = ctx.getTransform();
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = Math.min(1, malam * 1.4) * (0.86 + 0.14 * Math.sin(ms / 900)); ctx.drawImage(cache.lampu, 0, bob * t.d); ctx.restore();
}
